// สถานะโต๊ะแบบ event log (เหมือน jobchain) — แผนเต็ม: plans/03-table-state-events.md
//
// ความจริงอยู่ที่ RTDB stores/{storeId}/tableStatesEvents ที่เดียว
//   - ทุกเครื่อง POS (main / 2nd / terminal) append event แล้วฟัง onChildAdded ตัวเดียว
//   - reduce (เรียงตาม ts ก่อน) เป็น posStore.tableState + jobsStore.tablesInfo shape เดิม
//   - CLEAR (จ่ายเงินจนโต๊ะว่าง) มาทาง onChildAdded เหมือน event อื่น แล้วเครื่องนี้ทิ้งของเก่าของโต๊ะนั้นเอง
//     การลบ event เก่าบน RTDB มีไว้ให้ node เล็กเท่านั้น (reconnect ดาวน์โหลดทั้ง node ใหม่)
//   - IndexedDB เป็นแค่ outbox ของตัวที่ server ยังไม่ตอบรับ ไม่ถูกอ่านขึ้นจอ
//   - Main Cashier เขียนผล reduce ลง tableState ให้แอปอื่นอ่าน (plugins/tablesState.plugin.ts)

import _ from "lodash";
import { nanoid } from "nanoid";
import { defineStore } from "pinia";
import { watch } from "vue";
import { child, getDatabase, onChildAdded, onValue, ref, remove, set, update } from "firebase/database";
import { setDoc } from "firebase/firestore";
import { RunMode } from "~~/interface/Pos";
import { FIRESTORE_COLLECTIONS } from "~~/interface/FirestoreCollections";
import { TableStateEventType as T, type TableStateEvent } from "~~/interface/TableStateEvent";
import {
  compareEvents,
  isSessionMarker,
  outboxDecision,
  reduceTableStates,
  toTablesInfo,
  toTableState,
} from "~~/modules/pos/utils/tableStateReducer";
import { getTableStateOutbox, type TableStateOutbox } from "~~/modules/pos/utils/tableStateOutbox";
import { isDevReadOnly } from "~/modules/core/utils/devStoreOverride";

// ไม่เห็นเครื่องหมายรอบ OPEN_SHOP (ร้านปิดอยู่) → ถือว่าโหลดชุดแรกครบหลังต่อเน็ตได้เท่านี้
const LOADED_FALLBACK_MS = 3000;
const CLOSE_SHOP_TIMEOUT_MS = 5000;

// ไม่ต้อง reactive — อยู่นอก state
let watchingStoreId: string | null = null;
let loadedTimer: ReturnType<typeof setTimeout> | null = null;
let stopOpenShopWatch: (() => void) | null = null;
// ts สูงสุดที่เครื่องนี้เคยสร้างหรือเคยเห็น (ดู buildEvent)
let lastTs = 0;
let flushing = false;
// รอบที่ปิดไปแล้ว (openJobId) — event ของรอบเหล่านี้ไม่มีผล ไม่ว่า ts จะเป็นเท่าไร
// ตัดสินด้วยรอบ ไม่ใช่เวลา เพราะนาฬิกาแต่ละเครื่องคลาดกันได้
// (เครื่องนาฬิกาเร็วส่ง event ของรอบเก่าตามมาทีหลัง ts จะเลยเวลาปิดร้านไปแล้ว)
const closedSessions = new Set<string>();
// ts ของ CLOSE job ล่าสุด — ใช้กับ event ที่ไม่มี openJobId (ยังไม่รู้รอบตอนสร้าง) เท่านั้น
let closedTs = 0;

const isClosedEvent = (ev: TableStateEvent) =>
  ev.openJobId ? closedSessions.has(ev.openJobId) : ev.ts <= closedTs;

const serverNow = () => useServerTime().getCorrectedDayjsInstance().valueOf();

// โหมด Audit ดูอย่างเดียว — ด่านที่สองกันสถานะโต๊ะไม่ให้หลุดลง RTDB เหมือน jobs.add / cart (ดู composables/useAuditMode.ts)
// ด่านแรกคือปุ่มที่เรียก blockIfAudit แต่บางปุ่มไม่มี (เช่นเลือก channel) และ outbox ไม่ได้มาจากปุ่ม
// เช็คผ่าน useRunningMode ตรง ๆ ไม่ import useAuditMode (ดึง sweetalert2 มาด้วย เทสต์ใน node ใช้ไม่ได้)
const isAuditDevice = () => useRunningMode().getMode() === RunMode.AUDIT;

// RTDB ไม่รับ undefined
const clean = <V>(value: V): V => JSON.parse(JSON.stringify(value));

const recomputeLazy = _.debounce(() => useTableStatesEventsStore().recompute(), 50);

type EventExtra = Pick<TableStateEvent, "payload" | "reason" | "ref"> & { openJobId?: string | null };

export const useTableStatesEventsStore = defineStore("tableStatesEvents", {
  state: () => ({
    /** event ที่รู้จัก (จาก onChildAdded + ที่เครื่องนี้เพิ่งเขียน) key = event.id */
    events: {} as Record<string, TableStateEvent>,
    /** รอบปัจจุบัน = openJobId ของเครื่องหมายรอบ (OPEN_SHOP) ล่าสุด */
    sessionOpenJobId: null as string | null,
    sessionTs: 0,
    /** ts ของ CLEAR ล่าสุดต่อโต๊ะ (จากผล reduce) */
    clearedAt: {} as Record<string, number>,
    /** onChildAdded ชุดแรกเข้ามาครบแล้ว */
    remoteLoaded: false,
    /** เพิ่มทุกครั้งที่ reduce ใหม่ — projection plugin ฟังตัวนี้ */
    revision: 0,
    /** Main Cashier เขียน tableState ทับได้ (ดู syncLoadedFlag) */
    projectionReady: false,
  }),

  actions: {
    init() {
      const storeId = useStoreId();
      if (!storeId || watchingStoreId === storeId) return;
      watchingStoreId = storeId;
      this.watch();
      this.watchConnection();
    },

    // ------------------------------------------------------------------ อ่าน

    watch() {
      onChildAdded(
        useFirebase().tableStatesEventsRef(),
        (snapshot) => {
          const event = snapshot.val() as TableStateEvent;
          if (!event || typeof event.ts !== "number") return;
          this.receive({ ...event, id: snapshot.key as string });
          recomputeLazy(); // ชุดแรกมาเป็นร้อยตัวติดกันใน tick เดียว
        },
        (error) => useRtdbGuardStore().reportJobchainFailure("tableStatesEvents:onChildAdded", error)
      );
    },

    // path ในเครื่อง ไม่ดาวน์โหลดอะไร
    watchConnection() {
      onValue(ref(getDatabase(), ".info/connected"), (snapshot) => {
        if (snapshot.val() !== true) return;
        if (this.remoteLoaded) {
          this.flushOutbox();
          return;
        }
        // เริ่มนับตอนต่อเน็ตได้ ไม่ใช่ตอนบูต ไม่งั้นเครื่องที่บูตตอนเน็ตหลุดจะถูกตีว่าโหลดครบทั้งที่ยังไม่ได้อะไร
        if (loadedTimer) clearTimeout(loadedTimer);
        loadedTimer = setTimeout(() => this.markLoaded(), LOADED_FALLBACK_MS);
      });
    },

    receive(event: TableStateEvent) {
      if (isClosedEvent(event)) return;
      // จำ ts สูงสุดที่เห็น — event ที่เครื่องนี้สร้างหลังจากนี้ต้องเรียงอยู่หลังเสมอ (ดู buildEvent)
      lastTs = Math.max(lastTs, event.ts);
      this.events[event.id] = event;
      if (isSessionMarker(event)) this.onSessionMarker(event);
      if (event.type === T.CLEAR && event.table) this.dropBefore(event);
    },

    // เครื่องหมายรอบ (OPEN_SHOP) — onChildAdded ชุดแรกยิงครบใน tick เดียวเสมอ (วัดแล้ว แผนข้อ 4.8)
    // → setTimeout(0) หลังเห็น = ทุกตัวที่อยู่บน server เข้ามาครบแล้ว
    onSessionMarker(event: TableStateEvent) {
      // ไม่ลบ event ของรอบอื่นทิ้งที่นี่ — reducer กรองด้วยรอบอยู่แล้ว
      if (!this.sessionOpenJobId || event.ts >= this.sessionTs) {
        this.sessionOpenJobId = event.openJobId;
        this.sessionTs = event.ts;
      }
      this.syncLoadedFlag();
      if (!this.remoteLoaded) setTimeout(() => this.markLoaded(), 0);
    },

    // CLEAR → event เก่าของโต๊ะนี้ไม่มีผลแล้ว ทิ้งทั้งใน memory และ outbox (ได้ผลเท่ากับฟัง onChildRemoved)
    dropBefore(clear: TableStateEvent) {
      const isStale = (ev: TableStateEvent) => ev.table === clear.table && compareEvents(ev, clear) < 0;
      for (const [id, ev] of Object.entries(this.events)) {
        if (isStale(ev)) delete this.events[id];
      }
      this.withOutbox(async (outbox) => {
        const stale = (await outbox.getAll()).filter(isStale);
        await outbox.remove(stale.map((ev) => ev.id));
      });
    },

    markLoaded() {
      if (this.remoteLoaded) return;
      if (loadedTimer) clearTimeout(loadedTimer);
      loadedTimer = null;
      this.remoteLoaded = true;
      this.recompute();
      this.flushOutbox();
      this.watchOpenShop();
    },

    /**
     * tableStateLoaded (ปุ่ม Sync to Cloud) = โหลดชุดแรกครบ
     * projectionReady (Main Cashier เขียน tableState) เข้มกว่า: เครื่องหมายรอบต้องตรงกับ OPEN ใน jobchain
     * - jobchain อาจโหลดช้ากว่า node นี้ (ดู watchOpenShop)
     * - เครื่องที่ค้างรอบที่ปิดไปแล้ว (ยังไม่ reload) ห้ามเขียนทับ tableState ของรอบปัจจุบัน
     */
    syncLoadedFlag() {
      const openShop: any = useJobsStore().openShop;
      usePosStore().tableStateLoaded = this.remoteLoaded;
      this.projectionReady =
        this.remoteLoaded && !!this.sessionOpenJobId && openShop?.jobId === this.sessionOpenJobId;
    },

    recompute() {
      const result = reduceTableStates(Object.values(this.events), {
        sessionOpenJobId: this.sessionOpenJobId,
      });
      this.clearedAt = result.clearedAt;
      usePosStore().tableState = toTableState(result);
      const jobsStore = useJobsStore();
      jobsStore.tablesInfo = toTablesInfo(result);
      jobsStore.delayComputeFn(); // คิดเงินใหม่ (channel / sc / ส่วนลดท้ายบิล เปลี่ยน)
      this.revision++;
    },

    // ------------------------------------------------------------------ เขียน

    /** เพิ่ม event ของโต๊ะ — ขึ้นจอทันที แล้วค่อยส่งขึ้น RTDB (ผ่าน outbox) */
    appendEvent(table: string | null, type: T, extra: EventExtra = {}) {
      const event = this.buildEvent(table, type, extra);
      this.commit(event);
      return event;
    },

    /**
     * จ่ายเงินจนโต๊ะว่าง: CLEAR + ลบ event เก่าของโต๊ะใน update เดียว
     * ลบเฉพาะ id ที่เครื่องนี้รู้จัก ไม่กวาดทั้งโต๊ะ — event ใหม่ที่อีกเครื่องเพิ่งเขียนจะไม่โดนลบ
     */
    clearTable(table: string, extra: Pick<TableStateEvent, "reason" | "ref"> = {}) {
      const event = this.buildEvent(table, T.CLEAR, extra);
      this.commit(event, this.idsBefore(event));
      return event;
    },

    /**
     * Main Cashier รับ job จาก jobRequest (terminal / mobile-order-flutter ส่ง job ให้ Main Cashier เขียน jobchain แทน)
     * ผู้ส่งฝาก channel / แขกที่เลือกตอนเปิดโต๊ะใหม่มากับ job (`pendingTableState`) → เขียนเป็น PATCH ตัวเดียวที่นี่
     * ของเดิม terminal ไม่เคย flush ค่าที่พักไว้ channel ที่เลือกจาก terminal จึงหายเงียบ
     * Flutter เคยเขียน tableState ตรง (ทับทั้งโต๊ะ) — handoff: plans/04-flutter-pending-table-state.md
     *
     * source = job.orderChannel (terminal_order / employee_mobile) เก็บเป็น reason ไว้ย้อนดูว่ามาจากไหน
     */
    applyPendingTableState(table: string | null | undefined, pending: any, ref?: string, source?: string) {
      if (!table || !pending) return;
      const payload: TableStateEvent["payload"] = {};
      if (pending.guests != null) payload.guests = pending.guests;
      // channel เลือกได้ตอนโต๊ะยังว่างเท่านั้น — มีอยู่แล้ว = มีเครื่องอื่นเปิดโต๊ะนี้ไปก่อน ไม่ทับ
      if (pending.channel && !usePosStore().tableState[table]?.channel) payload.channel = pending.channel;
      if (_.isEmpty(payload)) return;
      this.appendEvent(table, T.PATCH, { payload, reason: source || "JOB_REQUEST", ref });
    },

    appendOpenShop(openJob: any) {
      if (!openJob?.jobId) return;
      return this.appendEvent(null, T.OPEN_SHOP, { openJobId: openJob.jobId });
    },

    buildEvent(table: string | null, type: T, extra: EventExtra = {}): TableStateEvent {
      // ts = max(เวลาเครื่อง, ts สูงสุดที่เคยเห็น + 1) — hybrid logical clock
      // - กดหลังจากเห็น event ของเครื่องอื่น = เรียงหลังเสมอ แม้นาฬิกาเครื่องนี้ช้ากว่า
      //   ไม่งั้นเครื่องนาฬิกาช้าปลดล็อคโต๊ะที่อีกเครื่องเพิ่งล็อคไม่ได้ และ CLEAR ตอนจ่ายเงินไม่ล้างโต๊ะ
      //   (ts ของ CLEAR ต่ำกว่า event ที่มีอยู่ reducer เลยเอา event เก่ามาใช้ต่อ)
      // - ตอน offline ไม่เห็น event ใหม่ จึงใช้เวลาเครื่อง = เรียงตามเวลาที่กดจริง ไม่ใช่เวลาที่ sync ถึง server
      // - เวลาเท่ากันจากเครื่องเดียวกัน ลำดับหลัง sort ไม่แน่นอน จึงต้อง +1
      const ts = Math.max(serverNow(), lastTs + 1);
      lastTs = ts;
      const by = this.identity();
      return clean({
        v: 1,
        id: `${ts}_${by.computerUuid || "pc"}_${nanoid(4)}`,
        table: table || undefined,
        type,
        ts,
        openJobId: extra.openJobId !== undefined ? extra.openJobId : this.currentOpenJobId(),
        payload: extra.payload,
        reason: extra.reason,
        ref: extra.ref,
        by,
      }) as TableStateEvent;
    },

    identity(): TableStateEvent["by"] {
      const employee = useEmployeePinStore().employee || ({} as any);
      return {
        computerUuid: useAuthStore().computerUuid || null,
        runMode: useRunningMode().getMode() || null,
        employeeId: employee.employeeId,
        employeeName: employee.name,
      };
    },

    currentOpenJobId(): string | null {
      return this.sessionOpenJobId ?? (useJobsStore().openShop as any)?.jobId ?? null;
    },

    idsBefore(clear: TableStateEvent) {
      return Object.values(this.events)
        .filter((ev) => ev.table === clear.table && compareEvents(ev, clear) < 0)
        .map((ev) => ev.id);
    },

    commit(event: TableStateEvent, deleteIds: string[] = []) {
      if (isAuditDevice()) return; // ไม่เปลี่ยนจอ ไม่เขียน
      // ของเราเองขึ้นจอทันที ไม่รอ onChildAdded ที่ SDK ยิงกลับมา (ได้ตัวเดียวกัน key เดียวกัน)
      this.receive(event);
      this.recompute();

      // dev สวมร้านอื่น = ดูอย่างเดียว เหมือน saveJobToCloud
      if (isDevReadOnly()) return;

      this.withOutbox((outbox) => outbox.put(event)).finally(() => this.writeRemote(event, deleteIds));
    },

    writeRemote(event: TableStateEvent, deleteIds: string[] = []) {
      const eventsRef = useFirebase().tableStatesEventsRef();
      const write = deleteIds.length
        ? update(eventsRef, {
            ...Object.fromEntries(deleteIds.map((id) => [id, null])),
            [event.id]: event,
          })
        : set(child(eventsRef, event.id), event);

      return write
        .then(() => this.withOutbox((outbox) => outbox.remove([event.id])))
        .catch((error) => {
          // ห้ามกลืน — firebase เขียนแบบ optimistic ถ้า server ปฏิเสธจะ rollback เงียบ ๆ (ดู rtdbGuard)
          // event ยังค้างใน outbox ไว้ส่งใหม่
          useRtdbGuardStore().reportJobchainFailure("tableStatesEvents:write", error);
        });
    },

    /** ส่ง outbox ที่ค้าง — หลังโหลดชุดแรกครบ (รู้รอบ + CLEAR แล้ว) และทุกครั้งที่ต่อเน็ตได้ใหม่ */
    async flushOutbox() {
      if (flushing || !this.remoteLoaded || isDevReadOnly() || isAuditDevice()) return;
      flushing = true;
      try {
        await this.withOutbox(async (outbox) => {
          this.recompute(); // clearedAt ล่าสุด
          const pending = (await outbox.getAll()).sort(compareEvents);
          const drop: string[] = [];
          const now = serverNow();
          for (const event of pending) {
            const decision = outboxDecision(event, {
              now,
              sessionOpenJobId: this.sessionOpenJobId,
              clearedAt: this.clearedAt,
            });
            if (decision === "drop" || isClosedEvent(event)) {
              drop.push(event.id);
              continue;
            }
            this.writeRemote(event, event.type === T.CLEAR ? this.idsBefore(event) : []);
          }
          await outbox.remove(drop);
        });
      } finally {
        flushing = false;
      }
    },

    async withOutbox<R>(fn: (outbox: TableStateOutbox) => Promise<R>): Promise<R | undefined> {
      try {
        return await fn(await getTableStateOutbox(useStoreId()));
      } catch (error) {
        console.warn("[tableState] outbox ใช้ไม่ได้", error);
        return undefined;
      }
    },

    // ------------------------------------------------------------------ รอบเปิด / ปิดร้าน

    /** ฟัง OPEN ของ jobchain (อาจโหลดช้ากว่า node นี้) — ปลดล็อค projection เมื่อตรงกับเครื่องหมายรอบ */
    watchOpenShop() {
      if (stopOpenShopWatch) return;
      this.syncLoadedFlag();
      stopOpenShopWatch = watch(
        () => useJobsStore().openShop,
        () => this.syncLoadedFlag()
      );
    },

    /**
     * เห็น CLOSE job (มาทาง jobchain ทุกเครื่อง) — การลบทั้ง node ตอนปิดร้านไม่ทำให้ onChildAdded ยิง
     * closedOpenJobId = jobId ของ OPEN ที่ CLOSE นี้ปิด (jobs.add หาให้) ไม่มีก็ใช้รอบปัจจุบันของเครื่องนี้
     */
    onCloseShop(closeJob: any, closedOpenJobId: string | null = null) {
      const closedId = closedOpenJobId ?? this.sessionOpenJobId;
      if (closedId) closedSessions.add(closedId);

      const ts = new Date(closeJob?.timestamp).getTime();
      if (ts && !isNaN(ts)) {
        closedTs = Math.max(closedTs, ts);
        lastTs = Math.max(lastTs, ts); // event ของรอบใหม่ต้องเรียงหลัง CLOSE
      }

      for (const [id, ev] of Object.entries(this.events)) {
        if (isClosedEvent(ev)) delete this.events[id];
      }
      if (this.sessionOpenJobId && closedSessions.has(this.sessionOpenJobId)) {
        this.sessionOpenJobId = null;
        this.sessionTs = 0;
      }
      this.syncLoadedFlag();
      this.recompute();
      this.withOutbox(async (outbox) => {
        const stale = (await outbox.getAll()).filter(isClosedEvent);
        await outbox.remove(stale.map((ev) => ev.id));
      });
    },

    /**
     * ปิดร้าน (Main Cashier ใน close.vue) — ลบ tableStatesEvents + tableState
     * ไม่ throw: พลาดแล้วรอบหน้ายังถูกต้อง (reducer กรองด้วย OPEN_SHOP ของรอบใหม่) แค่ node ใหญ่ขึ้น
     */
    async clearForCloseShop() {
      const fb = useFirebase();
      try {
        await Promise.race([
          Promise.all([
            remove(fb.tableStatesEventsRef()),
            remove(fb.tableStateRef()),
            setDoc(
              useFirebaseFirestore().subCollectionDocIdRef(FIRESTORE_COLLECTIONS.SYNC_DATA, FIRESTORE_COLLECTIONS.TABLE_STATE),
              {}
            ),
          ]),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), CLOSE_SHOP_TIMEOUT_MS)),
        ]);
      } catch (error) {
        console.warn("[tableState] ล้าง tableStatesEvents ตอนปิดร้านไม่สำเร็จ", error);
      }
      await this.withOutbox((outbox) => outbox.clear());
      this.events = {};
      this.recompute();
    },
  },
});
